"use client";

import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { findProfileGridSlot } from "@/utils/profile-grid";
import { Children, Fragment, isValidElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import cn from "twsx";

// Profile and overview bodies: a balanced masonry of section cards.

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
const MASONRY_MIN_COLUMN_WIDTH = 256;
const MASONRY_MAX_COLUMN_COUNT = 5;
const MASONRY_GAP = 16;
const MASONRY_ROW_HEIGHT = 4;
type MasonryPlacement = {
  column: number;
  columnSpan: number;
  rowStart: number;
  rowSpan: number;
};

/** Requests adjacent columns in an automatic profile grid; clamps on narrow screens. */
export function ProfileGridItem(t0) {
  const $ = _c(3);
  const {
    children,
    columnSpan: t1
  } = t0;
  const columnSpan = t1 === undefined ? 1 : t1;
  let t2;
  if ($[0] !== children || $[1] !== columnSpan) {
    t2 = <div data-profile-column-span={columnSpan}>{children}</div>;
    $[0] = children;
    $[1] = columnSpan;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function flattenProfileCards(children: ReactNode): ReactNode[] {
  const cards: ReactNode[] = [];
  Children.forEach(children, child => {
    if (child === null || child === undefined || typeof child === "boolean") {
      return;
    }
    if (isValidElement(child) && child.type === Fragment) {
      cards.push(...flattenProfileCards((child.props as {
        children?: ReactNode;
      }).children));
      return;
    }
    cards.push(child);
  });
  return cards;
}
function BalancedMasonry(t0) {
  const $ = _c(21);
  const {
    children,
    className
  } = t0;
  const containerRef = useRef(null);
  const [placements, setPlacements] = useState(null);
  let t1;
  if ($[0] !== children) {
    t1 = flattenProfileCards(children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cards = t1;
  let t2;
  if ($[2] !== cards.length) {
    t2 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const measure = () => {
        const width = container.getBoundingClientRect().width;
        const columnCount = Math.max(1, Math.min(MASONRY_MAX_COLUMN_COUNT, cards.length, Math.floor((width + MASONRY_GAP) / (MASONRY_MIN_COLUMN_WIDTH + MASONRY_GAP))));
        const columnRows = Array.from({
          length: columnCount
        }, _temp);
        const items = Array.from(container.querySelectorAll("[data-profile-grid-item]"));
        const nextPlacements = items.map(item => {
          const content = item.firstElementChild;
          if (!content) {
            return null;
          }
          const height = item.getBoundingClientRect().height;
          const {
            column,
            columnSpan,
            rowStart
          } = findProfileGridSlot(columnRows, content.getAttribute("data-profile-column-span") === "2" ? 2 : 1);
          const rowSpan = Math.max(1, Math.ceil((height + MASONRY_GAP) / MASONRY_ROW_HEIGHT));
          for (let index = column; index < column + columnSpan; index = index + 1, index) {
            columnRows[index] = rowStart + rowSpan;
          }
          return {
            column,
            columnSpan,
            rowStart,
            rowSpan
          };
        });
        setPlacements(current => {
          const unchanged = current?.length === nextPlacements.length && current.every((placement, index_0) => {
            const next = nextPlacements[index_0];
            if (placement === null || next === null) {
              return placement === next;
            }
            return next !== undefined && placement.column === next.column && placement.columnSpan === next.columnSpan && placement.rowStart === next.rowStart && placement.rowSpan === next.rowSpan;
          });
          return unchanged ? current : nextPlacements;
        });
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(container);
      container.querySelectorAll("[data-profile-grid-item]").forEach(item_0 => observer.observe(item_0));
      const mutationObserver = new MutationObserver(measure);
      mutationObserver.observe(container, {
        childList: true,
        subtree: true
      });
      return () => {
        observer.disconnect();
        mutationObserver.disconnect();
      };
    };
    $[2] = cards.length;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== cards.length || $[5] !== children) {
    t3 = [cards.length, children];
    $[4] = cards.length;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useIsomorphicLayoutEffect(t2, t3);
  const t4 = placements ? "grid w-full items-start gap-x-3 sm:gap-x-4" : "flex w-full flex-col gap-3 sm:gap-4";
  let t5;
  if ($[7] !== className || $[8] !== t4) {
    t5 = cn(t4, className);
    $[7] = className;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== placements) {
    t6 = placements ? {
      gridAutoRows: `${MASONRY_ROW_HEIGHT}px`,
      gridTemplateColumns: `repeat(${Math.max(1, Math.max(...placements.flatMap(_temp2)))}, minmax(0, 1fr))`
    } : undefined;
    $[10] = placements;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== cards || $[13] !== placements) {
    let t8;
    if ($[15] !== placements) {
      t8 = (card, index_1) => {
        const placement_1 = placements?.[index_1];
        const key = isValidElement(card) && card.key !== null ? String(card.key) : `profile-card-${index_1}`;
        return <div key={key} data-profile-grid-item={true} className="min-w-0" style={placement_1 === null ? {
          display: "none"
        } : placement_1 ? {
          gridColumnStart: placement_1.column + 1,
          gridColumnEnd: `span ${placement_1.columnSpan}`,
          gridRowEnd: `span ${placement_1.rowSpan}`,
          gridRowStart: placement_1.rowStart
        } : undefined}>{card}</div>;
      };
      $[15] = placements;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = cards.map(t8);
    $[12] = cards;
    $[13] = placements;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div ref={containerRef} data-profile-grid="auto" className={t5} style={t6}>{t7}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp2(placement_0) {
  return placement_0 ? [placement_0.column + placement_0.columnSpan] : [];
}
function _temp() {
  return 1;
}
export function ProfileGrid(t0) {
  const $ = _c(8);
  const {
    children,
    className,
    columns: t1
  } = t0;
  const columns = t1 === undefined ? "fixed" : t1;
  if (columns === "auto") {
    let t2;
    if ($[0] !== children || $[1] !== className) {
      t2 = <BalancedMasonry className={className}>{children}</BalancedMasonry>;
      $[0] = children;
      $[1] = className;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== className) {
    t2 = cn("grid w-full grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-3", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <div data-profile-grid="fixed" className={t2}>{children}</div>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function ProfileSection(t0) {
  const $ = _c(12);
  const {
    title,
    children,
    className,
    actionButton,
    icon
  } = t0;
  let t1;
  if ($[0] !== icon || $[1] !== title) {
    t1 = <CardTitle>{icon}{title}</CardTitle>;
    $[0] = icon;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== actionButton || $[4] !== t1) {
    t2 = <CardHeader justify="between">{t1}{actionButton}</CardHeader>;
    $[3] = actionButton;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children) {
    t3 = <CardContent>{children}</CardContent>;
    $[6] = children;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== className || $[9] !== t2 || $[10] !== t3) {
    t4 = <Card className={className}>{t2}{t3}</Card>;
    $[8] = className;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}